/**
 * Utilities that handle the communication with the host site when embedded.
 *
 * E.g. notifying the host site about changing height or calling actions.
 */

import type { PsiAnyMessage, PsiIframeDimensionMessage } from '@repo/shared/messages';
import { getIsInSidebar } from 'platform-specific/url';
import { useCallback, useEffect, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native-web';

function notifyHostSite<T extends PsiAnyMessage>(message: T) {
    if (!getIsInSidebar()) return;
    window.parent.postMessage(message, '*');
}

/**
 * Measures the size of rendered content
 * and sends dimension updates to the host site when embedded.
 *
 * Returns an `onLayout` handler that should be attached
 * to the root screen container.
 */
export function useNotifyHostAboutDimensionOnLayoutHandler() {
    const [contentHeight, setContentHeight] = useState<number>(0);
    const [contentWidth, setContentWidth] = useState<number>(0);

    const onLayout = useCallback((e: LayoutChangeEvent) => {
        const { height, width } = e.nativeEvent.layout;
        setContentHeight(height ?? 0);
        setContentWidth(width ?? 0);
    }, []);

    useEffect(() => {
        notifyHostSite<PsiIframeDimensionMessage>({
            type: 'psi-iframe-dimension',
            height: contentHeight,
            width: contentWidth,
        });
    }, [contentHeight, contentWidth]);

    return onLayout;
}
